import React, { useEffect, useMemo, useState } from "react";
import { FaHistory, FaSpinner, FaTimes } from "react-icons/fa";
import ActivityLogsService from "../services/ActivityLogsService";

const formatDateTime = (value) => {
  if (!value) return "N/A";
  return new Date(value).toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const FIELD_LABELS = {
  "accessControl.driver.enabled": "Enable Driver module",
  "accessControl.driver.fullAccess": "Access type",
  "accessControl.driver.quickUploadAccess": "Quick Upload",
  "accessControl.driver.addDriverAccess": "Add Driver",
  "accessControl.driver.phoneEdit": "Phone number edit",
  "accessControl.driver.hidePhoneNumber": "Hide the phone number",
  "accessControl.driver.dlStatus": "DL Section",
  "accessControl.driver.dlEditDetails": "DL Section - Edit details",
  "accessControl.driver.dlEditStatus": "DL Section - Edit status",
  "accessControl.driver.aadhaarStatus": "Aadhaar Section",
  "accessControl.driver.aadhaarEditDetails": "Aadhaar Section - Edit details",
  "accessControl.driver.aadhaarEditStatus": "Aadhaar Section - Edit status",
  "accessControl.driver.panStatus": "PAN Section",
  "accessControl.driver.panEditDetails": "PAN Section - Edit details",
  "accessControl.driver.panEditStatus": "PAN Section - Edit status",
  "accessControl.driver.drivingChoicesStatus": "Driving Choices Section",
  "accessControl.driver.drivingChoicesEditDetails": "Driving Choices - Edit details",
  "accessControl.driver.drivingChoicesEditStatus": "Driving Choices - Edit status",
  "accessControl.driver.allowedStatuses": "Allowed driver statuses",
  "accessControl.driver.actions.view": "Drivers list action - View",
  "accessControl.driver.actions.edit": "Drivers list action - Edit",
  "accessControl.driver.actions.delete": "Drivers list action - Delete",
  "accessControl.rides.enabled": "Enable Rides module",
  "accessControl.rides.fullAccess": "Rides access",
  "accessControl.rides.actions.view": "Rides action - View",
  "accessControl.rides.actions.edit": "Rides action - Edit",
  "accessControl.rides.actions.delete": "Rides action - Delete",
  "accessControl.taxesCommission.enabled": "Enable Tax & Commission module",
  "accessControl.taxesCommission.actions.view": "Tax & Commission action - View",
  "accessControl.taxesCommission.actions.edit": "Tax & Commission action - Edit",
  "accessControl.taxesCommission.actions.delete": "Tax & Commission action - Delete",
  fullName: "Full Name",
  emailId: "Email",
  phone: "Phone",
  city: "City",
  tripType: "Trip Type",
  carTransmission: "Car Transmission",
  carType: "Car Type",
  area: "Area",
  availableTimes: "Available Times",
  role: "Role",
  isActive: "Active Status",
  accountStatus: "Account Status",
  suspensionReason: "Suspension Reason",
  isTestRecord: "Test Record",
  status: "Status",
  startedAt: "Ride Start Time",
  completedAt: "Ride Complete Time",
  assignedTo: "Assigned To",
  assignedBy: "Assigned By",
  "metadata.reason": "Reason",
  paymentStatus: "Payment Status",
  paidAt: "Paid At",
  dryRun: "Dry Run",
  batchNumber: "Batch Number",
  batchSize: "Batch Size",
  batchCount: "Batch Count",
  totalMatched: "Total Matched",
  totalInCohort: "Total In Cohort",
  totalCallsAttempted: "Total Calls Attempted",
  successCount: "Success Count",
  failedCount: "Failed Count",
  sourceFileName: "Uploaded File",
  totalRows: "Total Rows",
  matchedRows: "Matched Rows",
  insertedRows: "Inserted Rows",
  duplicateRows: "Duplicate Rows",
  skippedUnmatchedRows: "Skipped Unmatched Rows",
  skippedInvalidRows: "Skipped Invalid Rows",
  enable_scheduled_rides: "Enable Scheduled Rides",
  enable_multi_stop: "Enable Multi Stop",
  enable_ride_sharing: "Enable Ride Sharing",
  maintenance_mode: "Maintenance Mode",
};

const STATUS_LABELS = {
  Pending: "Pending",
  Approved: "In Progress",
  Revisit: "In Revisit",
  Verified: "Verified",
  Onboarded: "Onboarded",
  Rejected: "Rejected",
};

const formatChange = (field) => {
  const key = String(field?.field || "field");
  const prettifyKey = (rawKey) =>
    String(rawKey || "")
      .split(".")
      .pop()
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());

  const normalizeStatusArray = (arr) =>
    arr.map((item) => STATUS_LABELS[item] || item).join(", ");

  const formatByField = (value) => {
    if (value === null || value === undefined || value === "") return "N/A";
    if (key === "accessControl.driver.fullAccess" || key === "accessControl.rides.fullAccess") {
      const boolVal =
        typeof value === "boolean" ? value : String(value).trim().toLowerCase() === "true";
      return boolVal ? "Full Access (all drivers, like Super Admin)" : "Limited Access";
    }
    if (Array.isArray(value)) {
      if (!value.length) return "N/A";
      if (key === "accessControl.driver.allowedStatuses") return normalizeStatusArray(value);
      if (key === "availableTimes") {
        return value
          .map((slot) => {
            if (slot && typeof slot === "object") {
              const day = slot.day || "";
              const start = slot.startTime || "";
              const end = slot.endTime || "";
              return `${day} ${start} - ${end}`.trim();
            }
            return String(slot);
          })
          .filter(Boolean)
          .join("; ");
      }
      return value.join(", ");
    }
    const isFeatureToggleKey =
      key.startsWith("enable_") ||
      key.endsWith("_mode") ||
      key.endsWith(".enabled");

    if (typeof value === "boolean") {
      if (isFeatureToggleKey) return value ? "Enabled" : "Disabled";
      if (key === "isActive") return value ? "Active" : "Inactive";
      if (key === "isTestRecord") return value ? "Yes" : "No";
      return value ? "Yes" : "No";
    }
    if (typeof value === "string") {
      const v = value.trim().toLowerCase();
      if (v === "true") {
        if (isFeatureToggleKey) return "Enabled";
        if (key === "isActive") return "Active";
        return "Yes";
      }
      if (v === "false") {
        if (isFeatureToggleKey) return "Disabled";
        if (key === "isActive") return "Inactive";
        return "No";
      }
      if (key === "accountStatus") {
        return value
          .replace(/[_-]+/g, " ")
          .replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
      }
      return value;
    }
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
  };

  const label = FIELD_LABELS[key] || prettifyKey(key);
  const before = formatByField(field?.before);
  const after = formatByField(field?.after);
  return `${label}: ${before} -> ${after}`;
};

const formatActionLabel = (value) => {
  if (!value) return "N/A";
  return String(value)
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
};

const ActivityLogsModal = ({ show, onClose, module, entityId }) => {
  const moduleOptions = [
    { value: "", label: "All Modules" },
    { value: "Admin", label: "Admins (Users)" },
    { value: "Employee", label: "Employees" },
    { value: "NewEmployee", label: "New Employees" },
    { value: "User", label: "Users" },
    { value: "Driver", label: "Drivers" },
    { value: "Ride", label: "All Rides" },
    { value: "RideStartTime", label: "Update Ride Start Time" },
    { value: "Role", label: "Roles" },
    { value: "Setting", label: "Settings" },
    { value: "TaxCommission", label: "Tax & Commission Reports" },
    { value: "CallBlasting", label: "Call Blasting" },
  ];

  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    startDate: "",
    endDate: "",
    userName: "",
    role: "",
    module: "",
    sortOrder: "desc",
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalLogs: 0,
    hasNext: false,
    hasPrev: false,
  });
  const defaultFilters = {
    page: 1,
    limit: 10,
    startDate: "",
    endDate: "",
    userName: "",
    role: "",
    module: "",
    sortOrder: "desc",
  };

  const title = useMemo(() => `${module} Activity Logs`, [module]);

  const fetchLogs = async (nextFilters = filters) => {
    if (!entityId) return;
    setLoading(true);
    try {
      const response =
        module === "Driver"
          ? await ActivityLogsService.getDriverLogs(entityId, nextFilters)
          : module === "Ride"
            ? await ActivityLogsService.getRideLogs(entityId, nextFilters)
            : await ActivityLogsService.getEmployeeLogs(entityId, nextFilters);
      const payload = response.data?.data || {};
      setLogs(payload.logs || []);
      setPagination(
        payload.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalLogs: 0,
          hasNext: false,
          hasPrev: false,
        }
      );
    } catch (error) {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!show) return;
    fetchLogs(filters);
  }, [show, entityId, module]);

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-5xl mx-4 max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800"
          >
            <FaTimes />
          </button>
        </div>

        <div className="p-4 border-b grid grid-cols-1 md:grid-cols-4 xl:grid-cols-8 gap-3">
          <input
            type="date"
            value={filters.startDate}
            onChange={(e) => setFilters((prev) => ({ ...prev, startDate: e.target.value }))}
            className="border rounded px-3 py-2 text-sm"
          />
          <input
            type="date"
            value={filters.endDate}
            onChange={(e) => setFilters((prev) => ({ ...prev, endDate: e.target.value }))}
            className="border rounded px-3 py-2 text-sm"
          />
          <input
            type="text"
            placeholder="Employee Name"
            value={filters.userName}
            onChange={(e) => setFilters((prev) => ({ ...prev, userName: e.target.value }))}
            className="border rounded px-3 py-2 text-sm"
          />
          <select
            value={filters.role}
            onChange={(e) => setFilters((prev) => ({ ...prev, role: e.target.value }))}
            className="border rounded px-3 py-2 text-sm"
          >
            <option value="">All Roles</option>
            <option value="superadmin">Super Admin</option>
            <option value="admin">Admin</option>
            <option value="developer">Developer</option>
            <option value="manager">Manager</option>
            <option value="employee">Employee</option>
            <option value="senior">Senior</option>
            <option value="junior">Junior</option>
          </select>
          <select
            value={filters.module}
            onChange={(e) => setFilters((prev) => ({ ...prev, module: e.target.value }))}
            className="border rounded px-3 py-2 text-sm"
          >
            {moduleOptions.map((opt) => (
              <option key={opt.value || "all-modules"} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <select
            value={filters.sortOrder}
            onChange={(e) => setFilters((prev) => ({ ...prev, sortOrder: e.target.value }))}
            className="border rounded px-3 py-2 text-sm"
          >
            <option value="asc">First to last</option>
            <option value="desc">Last to first</option>
          </select>
          <button
            type="button"
            onClick={() => {
              const next = { ...filters, page: 1 };
              setFilters(next);
              fetchLogs(next);
            }}
            className="bg-blue-600 text-white rounded px-3 py-2 text-sm"
          >
            Apply Filters
          </button>
          <button
            type="button"
            onClick={() => {
              const next = { ...defaultFilters };
              setFilters(next);
              fetchLogs(next);
            }}
            className="border border-gray-300 text-gray-700 rounded px-3 py-2 text-sm hover:bg-gray-50"
          >
            Clear
          </button>
        </div>

        <div className="p-4 overflow-auto flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-600">
              <FaSpinner className="animate-spin mr-2" /> Loading logs...
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center text-gray-500 py-16">No logs found</div>
          ) : (
            <div className="space-y-3">
              {logs.map((log) => (
                <div key={log._id} className="border rounded-lg p-3">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium inline-flex items-center gap-2">
                      <FaHistory className="text-indigo-500" />
                      {formatActionLabel(log.action)}
                    </span>
                    <span className="text-gray-500">{formatDateTime(log.timestamp)}</span>
                  </div>
                  <div className="text-sm text-gray-700 mt-1">{log.description}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    By {log.performedBy?.userName || "Unknown"} ({log.performedBy?.role || "N/A"})
                  </div>
                  <div className="text-xs text-gray-700 mt-2 space-y-1">
                    {(log.changedFields || []).map((field, idx) => (
                      <div key={`${log._id}-${idx}`}>{formatChange(field)}</div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t flex justify-between items-center">
          <span className="text-xs text-gray-600">
            {pagination.totalLogs || 0} logs
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={!pagination.hasPrev}
              onClick={() => {
                const next = { ...filters, page: Math.max(1, filters.page - 1) };
                setFilters(next);
                fetchLogs(next);
              }}
              className="px-3 py-1 text-sm border rounded disabled:opacity-50"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={!pagination.hasNext}
              onClick={() => {
                const next = { ...filters, page: filters.page + 1 };
                setFilters(next);
                fetchLogs(next);
              }}
              className="px-3 py-1 text-sm border rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityLogsModal;
